import {Box, MultiSelect, Text} from "@mantine/core";
import cx from "clsx";
import classes from "./ProtocolDashboard.module.css";
import commonClasses from "../../../../common/common.module.css";
import React, {useEffect, useState} from "react";
import {DashboardIcons} from "../../common/constants";
import {useParams} from "react-router";
import {IProtocol, TProtocolId} from "../../../types/protocol";
import {loadProtocol} from "../../../services/protocols";
import {useNeurotuneStore} from "../../../../common/store/neurotune";
import {ISubject} from "../../../../common/types";
import {ChartTypes} from "../charts";
import {HrHrvChart} from "../charts/HrHrvChart";
import {RmsSnrChart} from "../charts/RmsSnrChart";
import {SpikeCountsChart} from "../charts/SpikeCountsChart";
import {SpikesChart} from "../charts/SpikesChart";
import {DeltaConnectivityChart} from "../charts/DeltaConnectivityChart";
import {ConnectivityDistributionChart} from "../charts/ConnectivityDistributionChart";
import {ConnectivityMapChart} from "../charts/ConnectivityMapChart";
import {DeltaHRChart} from "../charts/DeltaHRChart";
import {BiomarkerDistributionChart} from "../charts/BiomarkerDistributionChart";
import {BiomarkerTimeseriesChart} from "../charts/BiomarkerTimeseriesChart";
import {SpectrumChart} from "../charts/SpectrumChart";
import {ImpedanceChart} from "../charts/ImpedanceChart";
import GridLayout, {useContainerWidth} from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import {IconGripHorizontal, IconRefresh} from "@tabler/icons-react";
import {isAria, isBios} from "@/common/utils/companies.ts";
import {IFrameChart} from "@/protocols/analysis/dashboards/charts/IFrameChart.tsx";

type LayoutItem = {
    i: string;
    x: number;
    y: number;
    w: number;
    h: number;
    minW?: number;
    minH?: number;
    maxW?: number;
    maxH?: number;
    static?: boolean;
};

interface ProtocolDashboardProps {
}

export const ProtocolDashboard: React.FC<ProtocolDashboardProps> = (_: ProtocolDashboardProps) => {
    const activeCompany = useNeurotuneStore((state) => state.activeCompany)
    const protocolId = useParams().protocol_id as TProtocolId
    const [selectedProtocol, setSelectedProtocol] = useState<IProtocol>()
    const subjects = useNeurotuneStore((state) => state.subjects)
    const [subject, setSubject] = useState<ISubject | undefined>()
    const [visibleCharts, setVisibleCharts] = useState<ChartTypes[]>([]);
    const defaultLayouts: Record<ChartTypes, { w: number; h: number; minW: number; minH: number }> = {
        [ChartTypes.HR_HRV]: {w: 24, h: 8, minW: 24, minH: 8},
        [ChartTypes.RMS_SNR]: {w: 24, h: 8, minW: 24, minH: 8},
        [ChartTypes.SPIKE_COUNTS]: {w: 24, h: 8, minW: 24, minH: 8},
        [ChartTypes.SPIKES]: {w: 48, h: 8, minW: 48, minH: 8},
        [ChartTypes.DELTA_CONNECTIVITY]: {w: 24, h: 8, minW: 24, minH: 8},
        [ChartTypes.DELTA_HR]: {w: 24, h: 8, minW: 24, minH: 8},
        [ChartTypes.CONNECTIVITY_DISTRIBUTION]: {w: 24, h: 8, minW: 24, minH: 8},
        [ChartTypes.CONNECTIVITY_MAP]: {w: 48, h: 10, minW: 24, minH: 10},
        [ChartTypes.BIOMARKER_DISTRIBUTION]: {w: 24, h: 8, minW: 24, minH: 8},
        [ChartTypes.BIOMARKER_TIMESERIES]: {w: 24, h: 8, minW: 24, minH: 8},
        [ChartTypes.SPECTRUM]: {w: 24, h: 8, minW: 24, minH: 8},
        [ChartTypes.IMPEDANCE]: {w: 24, h: 8, minW: 24, minH: 8},
        [ChartTypes.RESEARCH]: {w: 48, h: 16, minW: 24, minH: 8},
    };
    const [layout, setLayout] = useState<LayoutItem[]>([
        {i: ChartTypes.HR_HRV, x: 0, y: 0, ...defaultLayouts[ChartTypes.HR_HRV]},
        {i: ChartTypes.RMS_SNR, x: 0, y: 8, ...defaultLayouts[ChartTypes.RMS_SNR]},
        {i: ChartTypes.SPIKE_COUNTS, x: 0, y: 8, ...defaultLayouts[ChartTypes.SPIKE_COUNTS]},
        {i: ChartTypes.SPIKES, x: 0, y: 8, ...defaultLayouts[ChartTypes.SPIKES]},
        {i: ChartTypes.DELTA_CONNECTIVITY, x: 0, y: 8, ...defaultLayouts[ChartTypes.DELTA_CONNECTIVITY]},
        {i: ChartTypes.DELTA_HR, x: 0, y: 8, ...defaultLayouts[ChartTypes.DELTA_HR]},
        {i: ChartTypes.CONNECTIVITY_DISTRIBUTION, x: 0, y: 8, ...defaultLayouts[ChartTypes.CONNECTIVITY_DISTRIBUTION]},
        {i: ChartTypes.CONNECTIVITY_MAP, x: 0, y: 8, ...defaultLayouts[ChartTypes.CONNECTIVITY_MAP]},
        {i: ChartTypes.BIOMARKER_DISTRIBUTION, x: 0, y: 8, ...defaultLayouts[ChartTypes.BIOMARKER_DISTRIBUTION]},
        {i: ChartTypes.BIOMARKER_TIMESERIES, x: 0, y: 8, ...defaultLayouts[ChartTypes.BIOMARKER_TIMESERIES]},
        {i: ChartTypes.SPECTRUM, x: 0, y: 8, ...defaultLayouts[ChartTypes.SPECTRUM]},
        {i: ChartTypes.IMPEDANCE, x: 0, y: 8, ...defaultLayouts[ChartTypes.IMPEDANCE]},
        {i: ChartTypes.RESEARCH, x: 0, y: 16, ...defaultLayouts[ChartTypes.RESEARCH]},
    ]);
    const {width, containerRef, mounted} = useContainerWidth();
    const [chartRefreshTriggers, setChartRefreshTriggers] = useState<Record<ChartTypes, number>>({
        [ChartTypes.HR_HRV]: 0,
        [ChartTypes.RMS_SNR]: 0,
        [ChartTypes.SPIKE_COUNTS]: 0,
        [ChartTypes.SPIKES]: 0,
        [ChartTypes.DELTA_CONNECTIVITY]: 0,
        [ChartTypes.DELTA_HR]: 0,
        [ChartTypes.CONNECTIVITY_DISTRIBUTION]: 0,
        [ChartTypes.CONNECTIVITY_MAP]: 0,
        [ChartTypes.BIOMARKER_DISTRIBUTION]: 0,
        [ChartTypes.BIOMARKER_TIMESERIES]: 0,
        [ChartTypes.SPECTRUM]: 0,
        [ChartTypes.IMPEDANCE]: 0,
        [ChartTypes.RESEARCH]: 0
    })
    const [chartRotation, setChartRotation] = useState<Record<ChartTypes, number>>({
        [ChartTypes.HR_HRV]: 0,
        [ChartTypes.RMS_SNR]: 0,
        [ChartTypes.SPIKE_COUNTS]: 0,
        [ChartTypes.SPIKES]: 0,
        [ChartTypes.DELTA_CONNECTIVITY]: 0,
        [ChartTypes.DELTA_HR]: 0,
        [ChartTypes.CONNECTIVITY_DISTRIBUTION]: 0,
        [ChartTypes.CONNECTIVITY_MAP]: 0,
        [ChartTypes.BIOMARKER_DISTRIBUTION]: 0,
        [ChartTypes.BIOMARKER_TIMESERIES]: 0,
        [ChartTypes.SPECTRUM]: 0,
        [ChartTypes.IMPEDANCE]: 0,
        [ChartTypes.RESEARCH]: 0
    })
    const allCharts = [
        {value: ChartTypes.RMS_SNR, label: 'RMS/SNR Chart'},
        {value: ChartTypes.SPIKES, label: 'Spikes Chart'},
        {value: ChartTypes.DELTA_CONNECTIVITY, label: 'Delta Connectivity Chart'},
        {value: ChartTypes.DELTA_HR, label: 'Delta Heart Rate Chart'},
        {value: ChartTypes.CONNECTIVITY_DISTRIBUTION, label: 'Connectivity Distribution Chart'},
        {value: ChartTypes.CONNECTIVITY_MAP, label: 'Connectivity Map Chart'},
        {value: ChartTypes.HR_HRV, label: 'HR/HRV Chart'},
        {value: ChartTypes.BIOMARKER_DISTRIBUTION, label: 'Biomarker Distribution Chart'},
        {value: ChartTypes.BIOMARKER_TIMESERIES, label: 'Biomarker Timeseries Chart'},
        {value: ChartTypes.SPECTRUM, label: 'Spectrum Chart'},
        {value: ChartTypes.IMPEDANCE, label: 'Impedance Chart'},
    ];
    const biosCharts = [
        ...allCharts,
        {value: ChartTypes.RESEARCH, label: 'Research'}
    ]
    const ariaCharts = [
        {value: ChartTypes.RMS_SNR, label: 'RMS/SNR Chart'},
        {value: ChartTypes.SPIKES, label: 'Spikes Chart'},
        {value: ChartTypes.IMPEDANCE, label: 'Impedance Chart'},
        {value: ChartTypes.RESEARCH, label: 'Research'}
    ]
    let activeCharts = allCharts;
    if (isAria(activeCompany)) {
        activeCharts = ariaCharts
    } else if (isBios(activeCompany)) {
        activeCharts = biosCharts
    }


    // load the protocol that was passed to us in the parameter
    useEffect(() => {
        loadProtocol(activeCompany.id, protocolId, true).then((p) => {
            setSelectedProtocol(p)
            setSubject(subjects.find((s) => s.id === p.subjectId))
        }).catch(() => {
            // eat error since error notification would already be called
        })
    }, [])

    // Auto-position newly added charts to prevent overlapping
    // here we scan the grid from top-left to bottom-right to find the first available space
    // that the chart fits without overlapping another chart
    useEffect(() => {
        // Find charts that are visible but not in layout or have duplicate positions
        const layoutChartIds = new Set(layout.map(item => item.i));
        const newCharts = visibleCharts.filter(chartType => !layoutChartIds.has(chartType));

        if (newCharts.length > 0) {
            const overlaps = (a: { x: number, y: number, w: number, h: number }, b: {
                x: number,
                y: number,
                w: number,
                h: number
            }) => {
                return !(a.x + a.w <= b.x || b.x + b.w <= a.x || a.y + a.h <= b.y || b.y + b.h <= a.y);
            };

            let tempLayout = [...layout];
            for (const chartType of newCharts) {
                const defaults = defaultLayouts[chartType];
                let placed = false;
                for (let y = 0; !placed; y++) {
                    for (let x = 0; x <= 48 - defaults.w; x++) {
                        const candidate = {x, y, ...defaults};
                        const overlapsAny = tempLayout.some(existing => overlaps(candidate, existing));
                        if (!overlapsAny) {
                            tempLayout.push({i: chartType, ...candidate});
                            placed = true;
                            break;
                        }
                    }
                }
            }
            setLayout(tempLayout);
        }
    }, [visibleCharts])


    const handleLayoutChange = (newLayout: any) => {
        setLayout(Array.from(newLayout));
    };
    const refreshChart = (chartType: ChartTypes) => {
        setChartRotation(prev => ({...prev, [chartType]: prev[chartType] + 360}));
        setChartRefreshTriggers(prev => ({...prev, [chartType]: prev[chartType] + 1}));
    }

    const renderCharts = () => {
        const visibleLayout = layout.filter(item => visibleCharts.includes(item.i as ChartTypes));

        return (
            <Box ref={containerRef}>
                {mounted &&
                    <GridLayout
                        layout={visibleLayout}
                        width={width}
                        onLayoutChange={handleLayoutChange}
                        onResizeStop={() => window.dispatchEvent(new Event('resize'))}
                        autoSize={true}
                        dragConfig={{enabled: true, handle: ".drag-handle"}}
                        gridConfig={{cols: 48, rowHeight: 48}}
                    >
                        {visibleLayout.map((item) => {
                            const chartType = item.i as ChartTypes;

                            return (
                                <Box
                                    key={item.i}
                                    className={cx(classes.chartBox)}
                                >
                                    <Box style={{
                                        position: "absolute",
                                        left: "0.25em",
                                        top: "0.25em",
                                        width: "2em",
                                        height: "2em",
                                        zIndex: 10,
                                    }}>
                                        <IconGripHorizontal size={"1.5em"} cursor={"move"} className="drag-handle"
                                                            color={"var(--bios-color-text)"}/>
                                    </Box>
                                    <Box className={cx(commonClasses.dropshadowHover)} style={{
                                        position: "absolute",
                                        right: "0.25em",
                                        top: "0.25em",
                                        width: "1.5em",
                                        height: "1.5em",
                                        zIndex: 10,
                                    }}>
                                        <IconRefresh size={"1.5em"} cursor={"pointer"}
                                                     color={"var(--bios-color-text)"}
                                                     onClick={() => refreshChart(chartType)}
                                                     style={{
                                                         transform: `rotate(${chartRotation[chartType]}deg)`,
                                                         transition: "transform 1s ease-in-out"
                                                     }}
                                        />
                                    </Box>
                                    <Box style={{display: "grid", grid: "1fr /1fr",}}>
                                        {(() => {
                                            switch (chartType) {
                                                case ChartTypes.HR_HRV:
                                                    return (
                                                        <HrHrvChart
                                                            activeCompany={activeCompany}
                                                            selectedProtocol={selectedProtocol}
                                                            refreshTrigger={chartRefreshTriggers[chartType]}
                                                        />
                                                    );
                                                case ChartTypes.RMS_SNR:
                                                    return (
                                                        <RmsSnrChart
                                                            activeCompany={activeCompany}
                                                            selectedProtocol={selectedProtocol}
                                                            refreshTrigger={chartRefreshTriggers[chartType]}
                                                        />
                                                    );
                                                case ChartTypes.SPIKE_COUNTS:
                                                    return (
                                                        <SpikeCountsChart
                                                            activeCompany={activeCompany}
                                                            selectedProtocol={selectedProtocol}
                                                            refreshTrigger={chartRefreshTriggers[chartType]}
                                                        />
                                                    );
                                                case ChartTypes.SPIKES:
                                                    return (
                                                        <SpikesChart
                                                            activeCompany={activeCompany}
                                                            selectedProtocol={selectedProtocol}
                                                            refreshTrigger={chartRefreshTriggers[chartType]}
                                                        />
                                                    );
                                                case ChartTypes.DELTA_CONNECTIVITY:
                                                    return (
                                                        <DeltaConnectivityChart
                                                            activeCompany={activeCompany}
                                                            selectedProtocol={selectedProtocol}
                                                            refreshTrigger={chartRefreshTriggers[chartType]}
                                                        />
                                                    );
                                                case ChartTypes.DELTA_HR:
                                                    return (
                                                        <DeltaHRChart
                                                            activeCompany={activeCompany}
                                                            selectedProtocol={selectedProtocol}
                                                            refreshTrigger={chartRefreshTriggers[chartType]}
                                                        />
                                                    );
                                                case ChartTypes.CONNECTIVITY_DISTRIBUTION:
                                                    return (
                                                        <ConnectivityDistributionChart
                                                            activeCompany={activeCompany}
                                                            selectedProtocol={selectedProtocol}
                                                            refreshTrigger={chartRefreshTriggers[chartType]}
                                                        />
                                                    );
                                                case ChartTypes.CONNECTIVITY_MAP:
                                                    return (
                                                        <ConnectivityMapChart
                                                            activeCompany={activeCompany}
                                                            selectedProtocol={selectedProtocol}
                                                            refreshTrigger={chartRefreshTriggers[chartType]}
                                                        />
                                                    );
                                                case ChartTypes.BIOMARKER_DISTRIBUTION:
                                                    return (
                                                        <BiomarkerDistributionChart
                                                            activeCompany={activeCompany}
                                                            selectedProtocol={selectedProtocol}
                                                            refreshTrigger={chartRefreshTriggers[chartType]}
                                                        />
                                                    );
                                                case ChartTypes.BIOMARKER_TIMESERIES:
                                                    return (
                                                        <BiomarkerTimeseriesChart
                                                            activeCompany={activeCompany}
                                                            selectedProtocol={selectedProtocol}
                                                            refreshTrigger={chartRefreshTriggers[chartType]}
                                                        />
                                                    );
                                                case ChartTypes.SPECTRUM:
                                                    return (
                                                        <SpectrumChart
                                                            activeCompany={activeCompany}
                                                            selectedProtocol={selectedProtocol}
                                                            refreshTrigger={chartRefreshTriggers[chartType]}
                                                        />
                                                    );
                                                case ChartTypes.IMPEDANCE:
                                                    return (
                                                        <ImpedanceChart
                                                            activeCompany={activeCompany}
                                                            selectedProtocol={selectedProtocol}
                                                            refreshTrigger={chartRefreshTriggers[chartType]}
                                                        />
                                                    );
                                                case ChartTypes.RESEARCH:
                                                    return (
                                                        <IFrameChart
                                                            activeCompany={activeCompany}
                                                            selectedProtocol={selectedProtocol}
                                                            chart_name={ChartTypes.RESEARCH}
                                                        />
                                                    );
                                                default:
                                                    return (
                                                        <Box
                                                            style={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                height: "100%",
                                                            }}
                                                        >
                                                            <Text color="red">Invalid chart type</Text>
                                                        </Box>
                                                    );
                                            }
                                        })()}
                                    </Box>

                                </Box>
                            );
                        })}
                    </GridLayout>
                }
            </Box>
        )
    };

    return (
        <Box className={cx(classes.root)}>
            <Box className={cx(classes.content)}>
                <Box
                    className={cx(classes.header, commonClasses.sbRadius)}>
                    <Box style={{
                        display: "grid",
                        grid: "auto / auto 1fr auto",
                        justifyItems: "center",
                        alignItems: "center",
                        columnGap: "1em",
                        width: "100%",
                        borderBottom: "var(--bios-border)"
                    }}>
                        <Box className={cx("ntc-row-component")}>
                            <DashboardIcons.templateIcon/>
                            <Box ml={"0.5em"} className={cx(classes.title)}>Session Data Explorer</Box>
                        </Box>
                        <Box className={cx("ntc-row-component")}>
                            <Text> {selectedProtocol?.name}</Text>
                        </Box>
                        <Box className={cx("ntc-row-component")}>
                            <Text> {subject?.name}</Text>
                        </Box>
                    </Box>
                    <Box className={cx(classes.controlsBox)}>
                        <Box>Select Charts:</Box>
                        <MultiSelect
                            data={activeCharts}
                            hidePickedOptions
                            maxDropdownHeight={200}
                            value={visibleCharts}
                            onChange={(value) => {
                                setVisibleCharts(value as ChartTypes[]);
                            }}
                            placeholder="Pick charts to display"
                            className={cx(classes.multiSelect)}
                        />
                    </Box>
                </Box>

                {renderCharts()}
            </Box>
        </Box>
    )
}
